'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  Pagination,
  SearchInput,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDate,
} from '@rr/ui'
import { useCompanyContacts } from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 20

export default function ContactsList({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({ page, limit: PAGE_SIZE, search: search.trim() || undefined }),
    [page, search],
  )

  const { data, isLoading, isError, error } = useCompanyContacts(companyId, filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  return (
    <EntityListShell
      toolbar={
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1) }}
          placeholder="Search by name, email, phone…"
          className="max-w-sm"
        />
      }
      empty={!isLoading && items.length === 0}
      emptyState={
        isError
          ? error instanceof Error ? error.message : 'Failed to load contacts.'
          : 'No contacts in this organization.'
      }
      pagination={<Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Linked to</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => {
              const linkedTo = c.client?.name
                ? `Client · ${c.client.name}`
                : c.carrier?.name
                  ? `Carrier · ${c.carrier.name}`
                  : '—'
              return (
                <TableRow key={c.id}>
                  <TableCell className="font-medium text-foreground">
                    {c.name ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {c.contactPosition?.name ?? c.title ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm">{c.email ?? '—'}</TableCell>
                  <TableCell className="text-sm">{c.phone ?? c.mobile ?? '—'}</TableCell>
                  <TableCell className="text-sm">{linkedTo}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(c.createdAt)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}
